import { useState } from "react";
import { Link } from "react-router-dom";
import { Shield, FileText, Eye, AlertTriangle, Cookie, CheckCircle2, ExternalLink, CalendarDays, IdCard, Loader2 } from "lucide-react";
import { cn } from "@/lib/utils";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Calendar } from "@/components/ui/calendar";
import { format, differenceInYears } from "date-fns";
import { toast } from "sonner";

interface LegalAcceptanceModalProps {
  onAccept: (dob: Date) => void | Promise<{ error: Error | null } | void>;
}

const agreements = [
  {
    key: "tos",
    icon: FileText,
    title: "Terms of Service",
    summary:
      "By using Margin Metrics you agree to our terms governing account usage, data ownership, and platform rules. You retain ownership of your trade data.",
    color: "text-primary",
    bgColor: "",
    borderColor: "border-primary/20",
    link: "/terms",
  },
  {
    key: "privacy",
    icon: Eye,
    title: "Privacy Policy",
    summary:
      "We collect only the data necessary to run the platform (email, trade logs). Your data is encrypted, never sold, and you can request deletion at any time.",
    color: "text-white/60",
    bgColor: "",
    borderColor: "border-white/10",
    link: "/privacy",
  },
  {
    key: "risk",
    icon: AlertTriangle,
    title: "Risk Disclaimer",
    summary:
      "Trading involves substantial risk of loss. All analytics, AI insights, and signals are for educational purposes only — not financial advice. Past performance does not guarantee future results.",
    color: "text-warning",
    bgColor: "bg-warning/10",
    borderColor: "border-warning/20",
  },
  {
    key: "cookie",
    icon: Cookie,
    title: "Cookie Policy",
    summary:
      "We use essential cookies for authentication and your preferences (always on). With your consent, we also use analytics and advertising cookies (Reddit, Meta) to measure marketing — you can accept or reject these via the cookie banner.",
    color: "text-white/60",
    bgColor: "",
    borderColor: "border-white/10",
  },
];

export function LegalAcceptanceModal({ onAccept }: LegalAcceptanceModalProps) {
  const [checked, setChecked] = useState<Record<string, boolean>>({});
  const [dob, setDob] = useState<Date | undefined>(undefined);
  const [ageConfirmed, setAgeConfirmed] = useState(false);
  const [idDeclared] = useState(true); // auto-true until real ID verification is implemented
  const [ageError, setAgeError] = useState("");
  const [calendarOpen, setCalendarOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const allAgreementsChecked = agreements.every((a) => checked[a.key]);
  const isOver18 = dob ? differenceInYears(new Date(), dob) >= 18 : false;
  const allValid = allAgreementsChecked && isOver18 && ageConfirmed && idDeclared;

  const toggle = (key: string) => {
    setChecked((prev) => ({ ...prev, [key]: !prev[key] }));
  };

  const handleDobSelect = (date: Date | undefined) => {
    setDob(date);
    setAgeError("");
    if (date && differenceInYears(new Date(), date) < 18) {
      setAgeError("You must be at least 18 years old to use this platform.");
    }
    if (date) {
      setCalendarOpen(false);
    }
  };

  const handleAccept = async () => {
    if (!dob || !isOver18) {
      setAgeError("You must be at least 18 years old to use this platform.");
      return;
    }
    if (submitting) return;
    setSubmitting(true);
    try {
      const result = await onAccept(dob);
      // If the caller returned { error }, surface it.
      if (result && typeof result === "object" && "error" in result && result.error) {
        toast.error(result.error.message || "Failed to save your acceptance. Please try again.");
      }
    } catch (e) {
      const message = e instanceof Error ? e.message : "Something went wrong. Please try again.";
      // eslint-disable-next-line no-console
      console.error("[LegalAcceptanceModal] accept failed:", e);
      toast.error(message);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[60] overflow-y-auto">
      <div className="absolute inset-0 bg-black/80 backdrop-blur-sm" />

      <div className="relative flex min-h-full items-center justify-center p-4 py-8">
      <div className="relative w-full max-w-lg bg-[hsl(0,0%,7%)] border border-white/10 rounded-2xl shadow-2xl p-4 sm:p-6 animate-fade-in">
        {/* Header */}
        <div className="text-center mb-3 sm:mb-5">
          <Shield className="w-5 h-5 sm:w-7 sm:h-7 text-primary mx-auto mb-2 sm:mb-4" strokeWidth={1.5} />
          <h2 className="text-lg sm:text-2xl font-bold mb-0.5">Welcome to Margin Metrics</h2>
          <p className="text-xs sm:text-sm text-muted-foreground">
            Please review and accept the following to continue.
          </p>
        </div>

        {/* Agreements */}
        <div className="space-y-1.5 sm:space-y-2.5">
          {agreements.map((a) => (
            <button
              key={a.key}
              onClick={() => toggle(a.key)}
              className={cn(
                "w-full text-left p-2.5 sm:p-3.5 rounded-xl border transition-all duration-200",
                checked[a.key]
                  ? "border-profit/30 bg-profit/5"
                  : cn(a.borderColor, a.bgColor),
                "hover:scale-[1.01]"
              )}
            >
              <div className="flex items-start gap-3">
                {checked[a.key] ? (
                  <CheckCircle2 className="w-4 h-4 text-profit shrink-0 mt-0.5" strokeWidth={1.5} />
                ) : (
                  <a.icon className={cn("w-4 h-4 shrink-0 mt-0.5", a.color)} strokeWidth={1.5} />
                )}
                <div className="min-w-0">
                  <p
                    className={cn(
                      "text-sm font-semibold mb-0.5",
                      checked[a.key] ? "text-profit" : "text-foreground"
                    )}
                  >
                    {a.title}
                  </p>
                  <p className="text-xs text-muted-foreground leading-relaxed">
                    {a.summary}
                  </p>
                  {"link" in a && a.link && (
                    <Link
                      to={a.link}
                      target="_blank"
                      onClick={(e) => e.stopPropagation()}
                      className="inline-flex items-center gap-1 text-[11px] text-primary/70 hover:text-primary mt-1 transition-colors"
                    >
                      Read full policy <ExternalLink className="w-3 h-3" />
                    </Link>
                  )}
                </div>
              </div>
            </button>
          ))}
        </div>

        {/* Accept All shortcut */}
        {!allAgreementsChecked && (
          <button
            onClick={() => {
              const all: Record<string, boolean> = {};
              agreements.forEach((a) => (all[a.key] = true));
              setChecked(all);
            }}
            className="w-full mt-3 text-xs text-primary hover:text-primary/80 transition-colors text-center py-1"
          >
            Accept all agreements
          </button>
        )}

        {/* ═══ Age Verification Section ═══ */}
        <div className="mt-3 sm:mt-4 p-3 sm:p-4 rounded-xl border border-white/10 space-y-2 sm:space-y-3">
          <div className="flex items-center gap-2 mb-0.5">
            <IdCard className="w-3.5 h-3.5 sm:w-4 sm:h-4 text-white/60" strokeWidth={1.5} />
            <span className="text-xs sm:text-sm font-semibold text-foreground">Age Verification (18+)</span>
          </div>

          {/* Date of Birth picker */}
          <div>
            <label className="text-xs text-muted-foreground block mb-1.5">Date of Birth</label>
            <Popover open={calendarOpen} onOpenChange={setCalendarOpen} modal={true}>
              <PopoverTrigger asChild>
                <button type="button" className={cn(
                  "w-full flex items-center gap-2 px-3 py-2.5 rounded-lg border text-sm text-left transition-colors",
                  dob ? "border-border bg-background text-foreground" : "border-border/50 bg-muted/30 text-muted-foreground",
                  ageError && "border-destructive/50"
                )}>
                  <CalendarDays className="w-4 h-4 shrink-0" strokeWidth={1.5} />
                  {dob ? format(dob, "PPP") : "Select your date of birth"}
                </button>
              </PopoverTrigger>
              <PopoverContent className="w-auto p-0 pointer-events-auto z-[70]" align="center" side="bottom" sideOffset={4}>
                <Calendar
                  mode="single"
                  selected={dob}
                  onSelect={handleDobSelect}
                  disabled={(date) => date > new Date() || date < new Date("1900-01-01")}
                  defaultMonth={new Date(2000, 0)}
                  captionLayout="dropdown-buttons"
                  fromYear={1930}
                  toYear={new Date().getFullYear()}
                  initialFocus
                  className={cn("p-2 pointer-events-auto")}
                />
              </PopoverContent>
            </Popover>
            {ageError && (
              <p className="text-[11px] text-destructive mt-1 flex items-center gap-1">
                <AlertTriangle className="w-3 h-3" strokeWidth={1.5} /> {ageError}
              </p>
            )}
            {dob && isOver18 && (
              <p className="text-[11px] text-profit mt-1 flex items-center gap-1">
                <CheckCircle2 className="w-3 h-3" strokeWidth={1.5} /> Age verified: {differenceInYears(new Date(), dob)} years old
              </p>
            )}
          </div>

          {/* Age confirmation checkbox */}
          <button
            onClick={() => setAgeConfirmed(!ageConfirmed)}
            className={cn(
              "w-full flex items-start gap-2 sm:gap-3 p-2 sm:p-3 rounded-lg border transition-all text-left",
              ageConfirmed ? "border-profit/30 bg-profit/5" : "border-border/50 bg-muted/20"
            )}
          >
            <div className={cn("w-5 h-5 rounded border-2 shrink-0 mt-0.5 flex items-center justify-center transition-colors",
              ageConfirmed ? "border-profit bg-profit" : "border-muted-foreground/40"
            )}>
              {ageConfirmed && <CheckCircle2 className="w-3.5 h-3.5 text-profit-foreground" strokeWidth={1.5} />}
            </div>
            <span className="text-xs text-muted-foreground leading-relaxed">
              I confirm that I am <strong className="text-foreground">at least 18 years of age</strong> and meet the minimum age requirement to access financial trading tools.
            </span>
          </button>

          {/* ID verification — coming soon */}
        </div>

        {/* Continue button */}
        <button
          onClick={handleAccept}
          disabled={!allValid || submitting}
          className={cn(
            "w-full mt-3 sm:mt-4 py-2.5 sm:py-3.5 rounded-xl font-medium text-sm transition-all duration-300 flex items-center justify-center gap-2",
            allValid && !submitting
              ? "bg-primary text-primary-foreground hover:bg-primary/90 glow-primary"
              : "bg-secondary text-muted-foreground cursor-not-allowed"
          )}
        >
          {submitting && <Loader2 className="w-4 h-4 animate-spin" strokeWidth={1.5} />}
          {submitting
            ? "Saving…"
            : allValid
            ? "Continue to Margin Metrics"
            : "Complete all requirements to continue"}
        </button>

        <p className="text-[10px] text-muted-foreground text-center mt-3 leading-relaxed">
          By continuing you confirm you have read and agree to all of the above.
          You can review these policies anytime in Settings.
        </p>
      </div>
      </div>
    </div>
  );
}
